import React, { useState } from 'react';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Edit, Calendar, MapPin, Users, TrendingUp, Globe } from 'lucide-react';
import { EditProspectModal } from './EditProspectModal';
import { Prospect } from './types';
import { motion } from 'framer-motion';

export interface PipelineProspectDetailsProps {
  prospect: Prospect;
  onClose: () => void;
  onUpdate: (id: string, updatedProspect: Partial<Prospect>) => void;
}

const PipelineProspectDetails = ({
  prospect,
  onClose,
  onUpdate
}: PipelineProspectDetailsProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const detailItemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1 + i * 0.05,
        duration: 0.3
      }
    })
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with accent gradient */}
      <div className="relative">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500" />
        <SheetHeader className="border-b border-accent/10 pb-4 pt-6 px-6 mb-0 bg-background/50">
          <div className="flex items-center w-full">
            <SheetTitle className="text-xl font-semibold bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
              Prospect Details
            </SheetTitle>
          </div>
        </SheetHeader>
      </div>

      <div className="flex justify-between items-center mb-4 px-6 pt-4">
        <Button 
          onClick={() => setIsEditModalOpen(true)} 
          variant="outline" 
          className="gap-1 bg-gradient-to-r from-accent/5 to-transparent border-blue-500/30 text-foreground hover:bg-blue-500/10"
        >
          <Edit size={16} />
          Edit
        </Button>
      </div>

      <div className="space-y-6 flex-1 overflow-auto px-6 pb-6">
        {/* Company Name with animation */}
        <motion.div
          variants={detailItemVariants}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <div className="p-4 rounded-lg bg-accent/5 border border-accent/10 backdrop-blur-sm">
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Company Name</h3>
            <p className="text-xl font-medium bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
              {prospect.name}
            </p>
          </div>
        </motion.div>

        {/* Description */}
        <motion.div
          variants={detailItemVariants}
          initial="hidden"
          animate="visible"
          custom={1}
          className="bg-accent/5 rounded-lg p-4 border border-accent/10 backdrop-blur-sm"
        >
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Description</h3>
          <p className="text-sm">{prospect.description || "No description available"}</p>
        </motion.div>

        {/* Details Grid */}
        <motion.div
          variants={detailItemVariants}
          initial="hidden"
          animate="visible"
          custom={2}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {/* Location */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-accent/5 border border-accent/10 backdrop-blur-sm">
            <div className="mt-1">
              <MapPin size={18} className="text-blue-400" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground">Location</h4>
              <p className="font-medium">{prospect.location || "Not specified"}</p>
            </div>
          </div>

          {/* Industry */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-accent/5 border border-accent/10 backdrop-blur-sm">
            <div className="mt-1">
              <Calendar size={18} className="text-indigo-400" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground">Industry</h4>
              <p className="font-medium">{prospect.industry || "Not specified"}</p>
            </div>
          </div>

          {/* Employee Count */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-accent/5 border border-accent/10 backdrop-blur-sm">
            <div className="mt-1">
              <Users size={18} className="text-violet-400" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground">Employee Count</h4>
              <p className="font-medium">{prospect.employee_count || "Not specified"}</p>
            </div>
          </div>

          {/* Revenue Range */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-accent/5 border border-accent/10 backdrop-blur-sm">
            <div className="mt-1">
              <TrendingUp size={18} className="text-emerald-400" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground">Revenue Range</h4>
              <p className="font-medium">{prospect.revenue_range || "Not specified"}</p>
            </div>
          </div>

          {/* Website */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-accent/5 border border-accent/10 backdrop-blur-sm col-span-1 md:col-span-2">
            <div className="mt-1">
              <Globe size={18} className="text-cyan-400" />
            </div>
            <div className="w-full">
              <h4 className="text-sm font-medium text-muted-foreground">Website</h4>
              <p className="font-medium truncate w-full">{prospect.website ? (
                <a 
                  href={prospect.website.startsWith('http') ? prospect.website : `https://${prospect.website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline"
                >
                  {prospect.website}
                </a>
              ) : "Not specified"}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Edit Modal */}
      <EditProspectModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        prospect={prospect}
        onUpdate={onUpdate}
      />
    </div>
  );
};

export default PipelineProspectDetails;
